import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Plus, Minus, Trash, ShoppingCart } from '@phosphor-icons/react';
import * as engine from '@/lib/ordering/cartEngine';
import { formatINR } from '@/lib/money';
import { useCustomer, PageWrap } from './CustomerApp';

export default function CustomerCart() {
  const navigate = useNavigate();
  const { cart, updateCartQty, clearCart, selectedStore } = useCustomer();

  const totals = engine.computeTotals(cart);

  if (cart.length === 0) {
    return (
      <PageWrap>
        <div className="min-h-screen flex flex-col items-center justify-center px-6 pb-24" data-testid="cart-empty">
          <ShoppingCart size={64} weight="duotone" className="text-gray-200 mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-1">Your cart is empty</h2>
          <p className="text-sm text-gray-500 mb-6">Add items from the menu to get started</p>
          <button data-testid="browse-menu-btn" onClick={() => navigate('/order/menu')} className="px-6 py-3 bg-sspice text-white font-semibold rounded-full hover:bg-sspice-hover transition-colors">Browse Menu</button>
        </div>
      </PageWrap>
    );
  }

  return (
    <PageWrap>
      <div className="min-h-screen pb-40 bg-[#FAFAFA]" data-testid="cart-page">
        <div className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-gray-100 px-4 py-3">
          <div className="flex items-center justify-between max-w-3xl mx-auto">
            <div className="flex items-center gap-3">
              <button data-testid="cart-back-btn" onClick={() => navigate(-1)} className="p-1"><ArrowLeft size={20} className="text-gray-900" /></button>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Your Cart</h2>
                <p className="text-xs text-gray-500">{cart.length} item{cart.length > 1 ? 's' : ''} from {selectedStore?.name || 'store'}</p>
              </div>
            </div>
            <button data-testid="clear-cart-btn" onClick={clearCart} className="text-xs text-sspice font-semibold flex items-center gap-1"><Trash size={14} /> Clear</button>
          </div>
        </div>

        <div className="px-4 pt-4 space-y-3 max-w-3xl mx-auto">
          {cart.map((item, i) => {
            const unit = engine.unitPrice(item);
            return (
              <motion.div
                key={item.signature} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}
                className="flex items-start gap-3 p-3 bg-white border border-gray-100 rounded-2xl shadow-sm"
                data-testid={`cart-item-${item.signature}`}
              >
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm text-gray-900">{item.menu_item_name}</h3>
                  {item.variant?.name && <p className="text-[11px] text-gray-500">{item.variant.name}</p>}
                  {(item.modifiers || []).length > 0 && (
                    <p className="text-[11px] text-gray-500">{item.modifiers.map((m) => m.name).join(', ')}</p>
                  )}
                  {item.notes && <p className="text-[11px] text-gray-400 italic">“{item.notes}”</p>}
                  <p className="text-xs text-gray-500 mt-0.5">{formatINR(unit)} each</p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="flex items-center gap-1.5 bg-sspice rounded-full px-1 py-0.5">
                    <button data-testid={`cart-minus-${item.signature}`} onClick={() => updateCartQty(item.signature, item.qty - 1)} className="w-6 h-6 flex items-center justify-center text-white"><Minus size={14} weight="bold" /></button>
                    <span className="text-white text-xs font-bold min-w-[16px] text-center">{item.qty}</span>
                    <button data-testid={`cart-plus-${item.signature}`} onClick={() => updateCartQty(item.signature, item.qty + 1)} className="w-6 h-6 flex items-center justify-center text-white"><Plus size={14} weight="bold" /></button>
                  </div>
                  <span className="text-sm font-bold text-gray-900">{formatINR(engine.lineTotal(item))}</span>
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="mx-4 mt-6 p-4 bg-white border border-gray-100 rounded-2xl max-w-3xl md:mx-auto">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Order Summary</h3>
          <div className="flex justify-between text-sm mb-1"><span className="text-gray-500">Subtotal</span><span className="font-semibold text-gray-900">{formatINR(totals.subtotal)}</span></div>
          <div className="flex justify-between text-sm mb-1"><span className="text-gray-500">Taxes & delivery</span><span className="text-gray-500">Calculated at checkout</span></div>
          <div className="border-t border-gray-100 mt-2 pt-2 flex justify-between"><span className="font-bold text-gray-900">Estimated Total</span><span className="font-bold text-gray-900">{formatINR(totals.subtotal)}</span></div>
        </div>

        <div className="fixed bottom-20 left-4 right-4 z-40 max-w-md mx-auto">
          <button data-testid="proceed-checkout-btn" onClick={() => navigate('/order/checkout')} className="w-full py-4 bg-sspice hover:bg-sspice-hover text-white font-semibold rounded-2xl shadow-lg transition-colors">
            Proceed to Checkout &middot; {formatINR(totals.subtotal)}
          </button>
        </div>
      </div>
    </PageWrap>
  );
}
